/**
 * LEORI — mouvement et interactions.
 *
 * Contrat de ce fichier :
 *  · aucune dépendance externe, aucun listener `scroll` brut ;
 *  · on n'anime que `transform` et `opacity` (compositor) ;
 *  · `prefers-reduced-motion: reduce` court-circuite tout à l'entrée et rend
 *    chaque contenu visible — jamais un élément bloqué à l'état initial ;
 *  · chaque module est indépendant : l'absence d'un élément n'en casse aucun autre.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = motionQuery.matches;

  /* ----------------------------------------------------------------------
     Reduced motion : on révèle tout et on s'arrête là.
     ---------------------------------------------------------------------- */
  function revealEverything() {
    var hidden = document.querySelectorAll('.leori-reveal, .leori-reveal-media, .leori-rule--draw');
    for (var i = 0; i < hidden.length; i++) hidden[i].classList.add('is-in');

    var counters = document.querySelectorAll('[data-leori-counter]');
    for (var j = 0; j < counters.length; j++) {
      counters[j].textContent = counters[j].getAttribute('data-leori-counter');
    }

    var pre = document.querySelector('[data-leori-preloader]');
    if (pre && pre.parentNode) pre.parentNode.removeChild(pre);
  }

  if (reduced) {
    revealEverything();
    return;
  }

  /* ----------------------------------------------------------------------
     Utilitaires
     ---------------------------------------------------------------------- */
  var rafPending = false;
  var scrollTasks = [];

  function onScroll(fn) {
    scrollTasks.push(fn);
  }

  function flushScroll() {
    rafPending = false;
    var y = window.pageYOffset;
    for (var i = 0; i < scrollTasks.length; i++) scrollTasks[i](y);
  }

  var resizeTasks = [];
  var resizeTimer = null;

  function onResize(fn) {
    resizeTasks.push(fn);
  }

  window.addEventListener(
    'resize',
    function () {
      /* Le redimensionnement arrive en rafale : on ne réagit qu'une fois posé. */
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        for (var i = 0; i < resizeTasks.length; i++) resizeTasks[i]();
      }, 150);
    },
    { passive: true }
  );

  window.addEventListener(
    'scroll',
    function () {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(flushScroll);
    },
    { passive: true }
  );

  function observe(nodes, onEnter, options) {
    if (!('IntersectionObserver' in window) || !nodes.length) {
      for (var i = 0; i < nodes.length; i++) onEnter(nodes[i]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      for (var k = 0; k < entries.length; k++) {
        if (entries[k].isIntersecting) {
          onEnter(entries[k].target);
          io.unobserve(entries[k].target);
        }
      }
    }, options || { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

    for (var j = 0; j < nodes.length; j++) io.observe(nodes[j]);
  }

  /* ----------------------------------------------------------------------
     Preloader — une fois par session, sur une page déjà rendue.
     ---------------------------------------------------------------------- */
  function initPreloader() {
    var pre = document.querySelector('[data-leori-preloader]');
    if (!pre) return;

    var seen = false;
    try {
      seen = window.sessionStorage.getItem('leori-preloader') === '1';
    } catch (e) {
      seen = false;
    }

    function dismiss(delay) {
      window.setTimeout(function () {
        pre.classList.add('is-done');
        window.setTimeout(function () {
          if (pre.parentNode) pre.parentNode.removeChild(pre);
        }, 800);
      }, delay);
    }

    if (seen) {
      if (pre.parentNode) pre.parentNode.removeChild(pre);
      return;
    }

    try {
      window.sessionStorage.setItem('leori-preloader', '1');
    } catch (e) {
      /* mode privé : le preloader rejouera, sans conséquence */
    }

    dismiss(1500);
  }

  /* ----------------------------------------------------------------------
     Révélations au scroll — fade + translateY 26px, échelonnées.
     ---------------------------------------------------------------------- */
  function initReveals() {
    observe(document.querySelectorAll('.leori-reveal, .leori-rule--draw'), function (el) {
      el.classList.add('is-in');
    });

    /* Les médias révélés par `clip-path` ne peuvent pas s'observer eux-mêmes.
       Chrome fait entrer le `clip-path` dans le calcul de l'intersection : un
       élément masqué par `inset(100% 0 0)` a un rectangle d'intersection de
       zéro, donc `isIntersecting` reste faux, donc `.is-in` n'arrive jamais,
       donc le clip ne s'ouvre jamais. La photographie restait invisible pour
       toujours, et son `loading="lazy"` n'était même pas déclenché : le
       navigateur ne charge pas ce qu'il ne montrera pas.

       On observe donc le conteneur, qui lui n'est pas rogné, et on pose la
       classe sur le média. Repli sur l'élément lui-même s'il n'a pas de
       parent — l'observer alors ne marche pas mieux, mais ne marche pas moins. */
    var medias = document.querySelectorAll('.leori-reveal-media');
    for (var i = 0; i < medias.length; i++) {
      (function (media) {
        var sentinelle = media.parentElement || media;
        observe([sentinelle], function () {
          media.classList.add('is-in');
        });
      })(medias[i]);
    }
  }

  /* ----------------------------------------------------------------------
     Compteurs — 0 → valeur au premier passage.
     ---------------------------------------------------------------------- */
  function runCounter(el) {
    var raw = el.getAttribute('data-leori-counter') || '';
    var match = raw.match(/-?\d+(?:[.,]\d+)?/);

    if (!match) {
      el.textContent = raw;
      return;
    }

    var target = parseFloat(match[0].replace(',', '.'));
    var prefix = raw.slice(0, match.index);
    var suffix = raw.slice(match.index + match[0].length);
    var decimals = (match[0].split(/[.,]/)[1] || '').length;
    var start = null;
    var duration = 1400;

    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      // ease-out quart : la valeur ralentit en arrivant, jamais de dépassement
      var eased = 1 - Math.pow(1 - p, 4);
      var value = (target * eased).toFixed(decimals);
      el.textContent = prefix + value + suffix;
      if (p < 1) window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  function initCounters() {
    var nodes = document.querySelectorAll('[data-leori-counter]');
    observe(nodes, runCounter, { threshold: 0.4 });
  }

  /* ----------------------------------------------------------------------
     Header — collé en haut. Il ne s'efface à la descente que si le réglage
     de la section le demande ; sinon il reste visible en permanence.
     ---------------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('[data-leori-header]');
    if (!header) return;

    var masquer = header.getAttribute('data-leori-header-hide') === 'true';
    var last = window.pageYOffset;
    var threshold = 320;

    /* `is-solid` bascule le header transparent en ivoire dès le premier pixel
       de défilement, et fait passer le wordmark en or. */
    function paint(y) {
      header.classList.toggle('is-solid', y > 4);
      header.classList.toggle('is-scrolled', y > 12);
    }

    paint(window.pageYOffset);

    /* Le header a pu rester masqué d'un chargement précédent : on repart
       toujours d'un header visible. */
    header.classList.remove('is-hidden');

    onScroll(function (y) {
      paint(y);

      if (masquer) {
        if (y > threshold && y > last) {
          header.classList.add('is-hidden');
        } else if (y < last - 4) {
          header.classList.remove('is-hidden');
        }
      }
      last = y;
    });
  }

  /* ----------------------------------------------------------------------
     Barre sticky mobile — apparaît après le repère, s'efface près du footer.
     ---------------------------------------------------------------------- */
  function initSticky() {
    var bar = document.querySelector('[data-leori-sticky]');
    if (!bar || !('IntersectionObserver' in window)) return;

    var afterSel = bar.getAttribute('data-leori-sticky-after');
    var after = afterSel ? document.querySelector(afterSel) : null;
    var footer = document.querySelector('[data-leori-footer]');

    // Un repère déclaré mais introuvable veut dire que la barre n'a pas sa place
    // sur cette page : sur une collection, « Voir la sélection » n'a aucun sens.
    if (afterSel && !after) {
      bar.remove();
      return;
    }

    var pastTrigger = !afterSel;
    var atFooter = false;

    function sync() {
      bar.classList.toggle('is-visible', pastTrigger && !atFooter);
    }

    if (after) {
      new IntersectionObserver(function (entries) {
        pastTrigger = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        sync();
      }, { threshold: 0 }).observe(after);
    }

    if (footer) {
      new IntersectionObserver(function (entries) {
        atFooter = entries[0].isIntersecting;
        sync();
      }, { rootMargin: '0px 0px -10% 0px' }).observe(footer);
    }

    sync();
  }

  /* ----------------------------------------------------------------------
     Hero — progression du déploiement.

     Aucune interception du défilement : on lit simplement où en est la scène
     `sticky` dans sa piste, et on écrit une seule variable CSS. Tout le reste
     (échelle du média, écartement des titres, apparition du contenu) est du
     CSS pur qui en dépend.
     ---------------------------------------------------------------------- */
  function initHero() {
    var hero = document.querySelector('[data-leori-hero]');
    if (!hero) return;

    var runway = hero.querySelector('.leori-hero__runway');
    if (!runway) return;

    var last = -1;

    onScroll(function () {
      var rect = runway.getBoundingClientRect();
      // Distance parcourue par la piste, rapportée à sa course utile.
      var travel = rect.height - window.innerHeight;
      if (travel <= 0) return;

      var p = Math.min(Math.max(-rect.top / travel, 0), 1);
      // Deux décimales suffisent : évite de réécrire le style à chaque pixel.
      var rounded = Math.round(p * 100) / 100;
      if (rounded === last) return;
      last = rounded;
      hero.style.setProperty('--hero-p', rounded);
    });
  }

  /* ----------------------------------------------------------------------
     Parallaxe — transform uniquement, coupée sous 880px (coût inutile
     sur mobile, où l'amplitude n'est de toute façon pas perceptible).
     ---------------------------------------------------------------------- */
  function initParallax() {
    var nodes = document.querySelectorAll('[data-leori-parallax]');
    if (!nodes.length || window.innerWidth < 880) return;

    var items = [];
    for (var i = 0; i < nodes.length; i++) {
      items.push({
        el: nodes[i],
        speed: parseFloat(nodes[i].getAttribute('data-leori-parallax')) || 0.1
      });
    }

    onScroll(function (y) {
      for (var j = 0; j < items.length; j++) {
        var it = items[j];
        var rect = it.el.parentNode.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) continue;
        it.el.style.transform = 'translate3d(0,' + (y * it.speed).toFixed(2) + 'px,0)';
      }
    });
  }

  /* ----------------------------------------------------------------------
     Marquee — duplique la piste pour une boucle sans couture.
     La copie est aria-hidden : le lecteur d'écran n'entend le texte qu'une fois.
     ---------------------------------------------------------------------- */
  function initMarquees() {
    var tracks = document.querySelectorAll('[data-leori-marquee]');

    for (var i = 0; i < tracks.length; i++) {
      var track = tracks[i];
      if (track.getAttribute('data-leori-marquee-ready')) continue;

      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-leori-marquee');
      track.parentNode.appendChild(clone);
      track.setAttribute('data-leori-marquee-ready', '1');
    }
  }

  /* ----------------------------------------------------------------------
     Accordéons <details> — ouverture/fermeture glissée.
     Le contenu reste accessible sans JS : <details> fonctionne nativement.
     ---------------------------------------------------------------------- */
  function initAccordions() {
    var groups = document.querySelectorAll('[data-leori-accordion]');

    for (var i = 0; i < groups.length; i++) {
      (function (group) {
        group.addEventListener('toggle', function (e) {
          var item = e.target;
          if (item.tagName !== 'DETAILS' || !item.open) return;
          // Un seul volet ouvert à la fois
          var siblings = group.querySelectorAll('details');
          for (var s = 0; s < siblings.length; s++) {
            if (siblings[s] !== item) siblings[s].open = false;
          }
        }, true);
      })(groups[i]);
    }
  }

    

  

  /* ----------------------------------------------------------------------
     Catégories défilantes — avance continue sur une piste qui défile vraiment.

     Le mouvement n'est pas une translation CSS mais un `scrollLeft` incrémenté
     à chaque image. C'est plus de code, et c'est le bon choix : la piste reste
     un conteneur de défilement, donc le doigt, le trackpad et la roulette y
     fonctionnent d'origine, et le clavier atteint chaque carte sans qu'on ait
     à recalculer quoi que ce soit.

     Les cartes sont écrites deux fois. Au point de bascule on RETRANCHE la
     moitié de la piste au lieu de revenir à zéro : la position visible est
     alors identique avant et après, et la boucle ne se voit pas.
     ---------------------------------------------------------------------- */
  function initCatRails() {
    var rails = document.querySelectorAll('[data-leori-catrail]');

    for (var i = 0; i < rails.length; i++) {
      (function (wrap) {
        if (wrap.getAttribute('data-ready')) return;
        wrap.setAttribute('data-ready', '1');

        var piste = wrap.querySelector('[data-leori-catrail-track]');
        if (!piste) return;

        var prev = wrap.querySelector('[data-leori-catrail-prev]');
        var next = wrap.querySelector('[data-leori-catrail-next]');

        /* Le réglage est en dixièmes de pixel : la vitesse utile se joue en
           dessous du pixel par image, un curseur entier n'aurait pas suffi. */
        var vitesse = (Number(wrap.getAttribute('data-leori-catrail-speed')) || 0) / 10;
        var enPause = false;
        var visible = true;
        var frame = null;

        /* La position est tenue à part, en flottant. `scrollLeft` est arrondi
           par le navigateur : lui ajouter 0,4 px à chaque image revenait à lui
           ajouter zéro, et la piste ne bougeait jamais. On accumule donc
           nous-mêmes et on n'écrit qu'une valeur déjà entière. */
        var position = 0;

        function boucle() {
          if (!enPause && visible && vitesse > 0) {
            var moitie = piste.scrollWidth / 2;
            position += vitesse;
            if (moitie > 0 && position >= moitie) position -= moitie;
            piste.scrollLeft = Math.round(position);
          }
          frame = window.requestAnimationFrame(boucle);
        }

        /* Toute manipulation fait autorité : après un glissé, une flèche ou un
           défilement au doigt, la position reprend là où la cliente l'a
           laissée plutôt que de sauter à l'endroit calculé. */
        piste.addEventListener('scroll', function () {
          if (enPause) position = piste.scrollLeft;
        }, { passive: true });

        function pause() { enPause = true; }
        function reprendre() { enPause = false; }

        /* On suspend pendant la lecture et pendant toute manipulation : reprendre
           la main sur une piste qui glisse encore est désagréable. */
        wrap.addEventListener('pointerenter', pause);
        wrap.addEventListener('pointerleave', reprendre);
        wrap.addEventListener('focusin', pause);
        wrap.addEventListener('focusout', reprendre);
        piste.addEventListener('touchstart', pause, { passive: true });
        piste.addEventListener('touchend', reprendre, { passive: true });

        /* Rien ne tourne hors de l'écran. */
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            visible = entries[0].isIntersecting;
          }, { threshold: 0 }).observe(wrap);
        }

        /* ---- Glisser pour faire défiler ----
           Le pointeur est capturé pour que sortir du cadre en tirant ne relâche
           pas la prise, comme dans n'importe quel carrousel qu'on manipule.

           Mais la prise n'est demandée QU'À PARTIR DU MOMENT où c'est vraiment
           un glissé. Prise dès `pointerdown`, elle retargetait le `click` sur
           la piste : le lien de la carte ne recevait jamais l'événement, et un
           clic simple à la souris n'ouvrait pas la collection. Le tactile, lui,
           marchait — il sort avant d'arriver ici, ce qui rendait le défaut
           invisible sur téléphone et bien réel sur ordinateur. */
        var tire = false, departX = 0, departScroll = 0, bouge = 0, pris = false;

        piste.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'touch') return;   /* le tactile défile déjà seul */
          tire = true; bouge = 0; pris = false;
          departX = e.clientX;
          departScroll = piste.scrollLeft;
        });

        piste.addEventListener('pointermove', function (e) {
          if (!tire) return;
          var d = e.clientX - departX;
          bouge = Math.max(bouge, Math.abs(d));

          /* Au-delà du seuil, l'intention n'est plus un clic : on prend la
             main sur le pointeur et on marque la piste. Le seuil est le même
             que celui du garde-fou de clic, plus bas — les deux décrivent la
             même frontière entre cliquer et tirer. */
          if (!pris && bouge > 8) {
            pris = true;
            piste.classList.add('is-dragging');
            try { piste.setPointerCapture(e.pointerId); } catch (err) {}
          }

          piste.scrollLeft = departScroll - d;
        });

        function relacher(e) {
          if (!tire) return;
          tire = false;
          piste.classList.remove('is-dragging');
          if (pris) { try { piste.releasePointerCapture(e.pointerId); } catch (err) {} }
          pris = false;
        }

        piste.addEventListener('pointerup', relacher);
        piste.addEventListener('pointercancel', relacher);

        /* Un glissé se termine sur un lien : sans ce garde-fou, relâcher après
           avoir tiré ouvrirait la carte sous le curseur. */
        piste.addEventListener('click', function (e) {
          if (bouge > 8) { e.preventDefault(); e.stopPropagation(); }
        }, true);

        /* ---- Flèches ---- */
        function pas(sens) {
          var carte = piste.querySelector('.leori-catrail__cell');
          var large = carte ? carte.getBoundingClientRect().width + 16 : piste.clientWidth * 0.8;
          piste.scrollBy({ left: large * sens, behavior: 'smooth' });
        }

        if (prev) prev.addEventListener('click', function () { pas(-1); });
        if (next) next.addEventListener('click', function () { pas(1); });

        if (vitesse > 0) frame = window.requestAnimationFrame(boucle);
      })(rails[i]);
    }
  }

  /* ----------------------------------------------------------------------
     Démarrage
     ---------------------------------------------------------------------- */
  function boot() {
    initPreloader();

    initReveals();
    initCatRails();
    initCounters();
    initHeader();
    initHero();
    initSticky();
    initParallax();
    initMarquees();
    initAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Si la préférence change en cours de session, on rend tout visible. */
  var onChange = function () {
    if (motionQuery.matches) revealEverything();
  };
  if (motionQuery.addEventListener) motionQuery.addEventListener('change', onChange);
  else if (motionQuery.addListener) motionQuery.addListener(onChange);

  /* L'éditeur de thème recharge des sections : on ré-arme les modules. */
  document.addEventListener('shopify:section:load', boot);
})();
